import {ProjectFile} from '../../api';

/**
 * Walks the FULL ancestor chain of `file` (parent, grandparent, ... up to the root) and pushes any
 * missing folder onto `targetFiles`, looking each one up in `sourcePool`.
 *
 * Folders never carry a `fileStatus` (the backend never sets one on folder creation), so callers that
 * filter `sourcePool` down to e.g. READY files before building `targetFiles` lose every folder and rely
 * entirely on this backfill to make the tree walkable again. A naive single-pass `Array.forEach` over
 * `targetFiles` only restores the immediate parent, because `forEach` never revisits elements pushed
 * onto the array during its own iteration - folders more than one level removed from an already-present
 * node silently stay missing, making deeply nested files unreachable in tree UIs that navigate strictly
 * by `parentId` (e.g. bic-file-manager). The `visitedIds` guard protects against malformed/circular
 * `parentId` data.
 */
export function addMissingAncestorFolders(
  targetFiles: ProjectFile[],
  sourcePool: ProjectFile[],
  file: ProjectFile,
): void {
  let parentId = file.parentId;
  const visitedIds = new Set<string>();

  while (parentId) {
    if (visitedIds.has(parentId)) {
      break;
    }
    visitedIds.add(parentId);

    const folder = sourcePool.find(sourceFile => sourceFile.id === parentId);
    if (!folder) {
      break;
    }

    if (!targetFiles.some(existingFile => existingFile.id === folder.id)) {
      targetFiles.push(folder);
    }

    parentId = folder.parentId;
  }
}
